import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface HubSimulation {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  profile_id: string;
  status: string;
  sim_type: string;
  sim_params: Record<string, any>;
  financial_params: Record<string, any> | null;
  results: Record<string, any> | null;
  notes: string | null;
  org_id: string | null;
  org_name?: string | null;
  creator_name?: string | null;
  version: number;
  parent_id: string | null;
}

export function useHubSimulations() {
  const queryClient = useQueryClient();

  const { data: simulations, isLoading, error } = useQuery({
    queryKey: ['hub-simulations'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hub_simulations')
        .select('*, organizations(name), profiles!created_by(display_name, email)')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data as any[]).map((row: any) => ({
        ...row,
        org_name: row.organizations?.name ?? null,
        creator_name: row.profiles?.display_name ?? row.profiles?.email ?? null,
        organizations: undefined,
        profiles: undefined,
      })) as HubSimulation[];
    },
  });

  const createSimulation = useMutation({
    mutationFn: async (params: {
      name: string;
      profile_id: string;
      sim_type: string;
      sim_params: Record<string, any>;
      financial_params?: Record<string, any>;
      org_id?: string | null;
      version?: number;
      parent_id?: string | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from('hub_simulations')
        .insert(params)
        .select('*, organizations(name)')
        .single();
      if (error) throw error;
      return { ...data, org_name: data.organizations?.name ?? null, organizations: undefined } as HubSimulation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hub-simulations'] });
      toast.success('Simulatie aangemaakt');
    },
    onError: (e: Error) => toast.error(`Fout: ${e.message}`),
  });

  const updateSimulation = useMutation({
    mutationFn: async ({ id, ...params }: {
      id: string;
      name?: string;
      sim_params?: Record<string, any>;
      financial_params?: Record<string, any>;
      results?: Record<string, any>;
      notes?: string;
      org_id?: string | null;
    }) => {
      const { data, error } = await (supabase as any)
        .from('hub_simulations')
        .update(params)
        .eq('id', id)
        .select('*, organizations(name)')
        .single();
      if (error) throw error;
      return { ...data, org_name: data.organizations?.name ?? null, organizations: undefined } as HubSimulation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hub-simulations'] });
      toast.success('Simulatie opgeslagen');
    },
    onError: (e: Error) => toast.error(`Fout: ${e.message}`),
  });

  const deleteSimulation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('hub_simulations')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hub-simulations'] });
      toast.success('Simulatie verwijderd');
    },
    onError: (e: Error) => toast.error(`Fout: ${e.message}`),
  });

  const createVersion = useMutation({
    mutationFn: async (source: HubSimulation) => {
      const { data, error } = await (supabase as any)
        .from('hub_simulations')
        .insert({
          name: source.name,
          profile_id: source.profile_id,
          sim_type: source.sim_type,
          sim_params: source.sim_params,
          financial_params: source.financial_params,
          results: source.results,
          org_id: source.org_id,
          version: source.version + 1,
          parent_id: source.id,
        })
        .select('*, organizations(name)')
        .single();
      if (error) throw error;
      return { ...data, org_name: data.organizations?.name ?? null, organizations: undefined } as HubSimulation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hub-simulations'] });
      toast.success('Nieuwe versie aangemaakt');
    },
    onError: (e: Error) => toast.error(`Fout: ${e.message}`),
  });

  return { simulations, isLoading, error, createSimulation, updateSimulation, deleteSimulation, createVersion };
}
